// Contas do acerto do colaborador (motorista ou agenciador parceiro): o que
// ele ganhou em cada viagem pelas regras cadastradas (comissão, valor por
// carga e por viagem, diárias, repasse de estadia), os agenciamentos, o fixo
// e o saldo. Tudo em centavos, para não errar arredondamento.
//
//   saldo = saldo anterior + vales − despesas pagas por ele − créditos + descontos
//   saldo > 0: o colaborador devolve · saldo < 0: a empresa paga
import { diasEntre } from './datas.js';
import { somarMeses } from './despesas.js';
import { data as dataBR, dinheiro, mesCurto, numeroBR } from './formato.js';
import { liquidoDoFrete } from './fretes.js';

const centavos = (v) => Math.round((Number(v) || 0) * 100);
const reais = (c) => c / 100;
const crescente = (campo) => (a, b) => (a[campo] < b[campo] ? -1 : a[campo] > b[campo] ? 1 : 0);

function valendo(regra, dia) {
  return !regra.deleted_at && regra.vigente_de <= dia && (!regra.vigente_ate || regra.vigente_ate >= dia);
}

// A regra do componente valendo no dia. A mais específica vence:
// colaborador + veículo > colaborador > veículo > padrão da empresa.
export function regraValendo(regras, componente, { colaboradorId, veiculoId = null, dia }) {
  const peso = (r) => (r.colaborador_id ? 2 : 0) + (r.veiculo_id ? 1 : 0);
  return regras
    .filter((r) => r.componente === componente && valendo(r, dia)
      && (!r.colaborador_id || r.colaborador_id === colaboradorId)
      && (!r.veiculo_id || r.veiculo_id === veiculoId))
    .sort((a, b) => peso(b) - peso(a))[0] ?? null;
}

// Percentual de um valor, em reais com centavos (meio centavo arredonda para cima).
export function porcentagem(valor, pct) {
  return reais(Math.round((centavos(valor) * Number(pct)) / 100));
}

const pctTexto = (p) => `${numeroBR(p)}%`;

// Créditos de uma viagem para o motorista dela, uma linha por item:
//   comissão e valor por carga (na regra do dia do carregamento), repasse de
//   estadia, valor por viagem e diárias (na regra do dia da saída).
// `cargas`: as cargas que entram agora (a viagem em andamento entra em
//   partes: as cargas carregadas até a data do acerto).
// `jaLancados`: créditos já lançados para esta viagem, em qualquer acerto
//   (não repete o mesmo tipo; as diárias continuam do dia seguinte ao último
//   pago).
// `ate`: data do acerto; as diárias da viagem em andamento vão até ela.
export function creditosDaViagem(viagem, cargas, regras, { jaLancados = [], ate = null } = {}) {
  const linhas = [];
  const quem = { colaboradorId: viagem.motorista_id, veiculoId: viagem.veiculo_id };
  const daViagem = jaLancados.filter((l) => !l.deleted_at && l.viagem_id === viagem.id);
  const ja = (subtipo, freteId = null) => daViagem.some((l) => l.subtipo === subtipo
    && (freteId === null || !l.frete_id || l.frete_id === freteId));
  const base = { viagem_id: viagem.id };

  for (const f of [...cargas].filter((x) => !x.deleted_at).sort(crescente('data_carregamento'))) {
    const dia = f.data_carregamento;
    const daCarga = `carga de ${dataBR(dia)}`;

    const comissao = regraValendo(regras, 'comissao_frete', { ...quem, dia });
    const pct = f.comissao_pct ?? comissao?.percentual ?? null;
    if (pct !== null && Number(pct) > 0 && !ja('comissao', f.id)) {
      const liquido = comissao?.base === 'liquido';
      const comEstadia = Boolean(comissao?.inclui_estadia) && Number(f.estadia_valor) > 0;
      const sobre = reais(centavos(liquido ? liquidoDoFrete(f) : f.valor_frete) + (comEstadia ? centavos(f.estadia_valor) : 0));
      linhas.push({
        ...base,
        subtipo: 'comissao',
        frete_id: f.id,
        data: dia,
        valor: porcentagem(sobre, pct),
        descricao: `Comissão de ${pctTexto(pct)} sobre ${dinheiro(sobre)} (${liquido ? 'frete líquido' : 'frete bruto'}${comEstadia ? ' + estadia' : ''}) · ${daCarga}`,
      });
    }

    const porCarga = regraValendo(regras, 'valor_por_carga', { ...quem, dia });
    if (porCarga && Number(porCarga.valor) > 0 && !ja('valor_por_carga', f.id)) {
      linhas.push({ ...base, subtipo: 'valor_por_carga', frete_id: f.id, data: dia, valor: Number(porCarga.valor), descricao: `Valor por carga · ${daCarga}` });
    }

    const estadia = Number(f.estadia_valor) || 0;
    const repasse = estadia > 0 ? regraValendo(regras, 'repasse_estadia', { ...quem, dia }) : null;
    if (repasse && !ja('repasse_estadia', f.id)) {
      const temPct = repasse.percentual !== null && repasse.percentual !== undefined;
      const valor = temPct ? porcentagem(estadia, repasse.percentual) : Number(repasse.valor);
      if (valor > 0) {
        linhas.push({
          ...base,
          subtipo: 'repasse_estadia',
          frete_id: f.id,
          data: dia,
          valor,
          descricao: `Repasse da estadia${temPct ? ` (${pctTexto(repasse.percentual)} de ${dinheiro(estadia)})` : ''} · ${daCarga}`,
        });
      }
    }
  }

  const saida = viagem.data_saida;
  const porViagem = regraValendo(regras, 'valor_por_viagem', { ...quem, dia: saida });
  if (porViagem && Number(porViagem.valor) > 0 && !ja('valor_por_viagem')) {
    linhas.push({ ...base, subtipo: 'valor_por_viagem', frete_id: null, data: saida, valor: Number(porViagem.valor), descricao: 'Valor por viagem' });
  }

  const diaria = regraValendo(regras, 'diaria', { ...quem, dia: saida });
  const periodo = diaria && Number(diaria.valor) > 0 ? diasAPagar(viagem, ate, daViagem) : null;
  if (periodo) {
    linhas.push({
      ...base,
      subtipo: 'diaria',
      frete_id: null,
      // A data do crédito é o último dia pago: o próximo acerto continua dali.
      data: periodo.fim,
      valor: reais(centavos(diaria.valor) * periodo.dias),
      descricao: `Diárias: ${periodo.dias} × ${dinheiro(diaria.valor)} (${dataBR(periodo.inicio)} a ${dataBR(periodo.fim)})`,
    });
  }
  return linhas;
}

// Dias da viagem, contando a saída e a chegada (saiu dia 1, voltou dia 3 = 3).
export function diasDeViagem(viagem) {
  return diasEntre(viagem.data_saida, viagem.data_chegada) + 1;
}

// Diárias ainda não pagas: do dia seguinte à última diária paga (ou da saída)
// até a chegada, ou até a data do acerto se a viagem não tinha voltado até
// ela. Contam o primeiro e o último dia. Devolve { inicio, fim, dias } ou null.
export function diasAPagar(viagem, ate, creditos = []) {
  const pagas = creditos
    .filter((l) => !l.deleted_at && l.viagem_id === viagem.id && l.subtipo === 'diaria')
    .map((l) => l.data)
    .sort();
  const inicio = pagas.length > 0 ? diaSeguinte(pagas.at(-1)) : viagem.data_saida;
  const voltou = Boolean(viagem.data_chegada) && (!ate || viagem.data_chegada <= ate);
  const fim = voltou ? viagem.data_chegada : ate;
  if (!fim || fim < inicio) return null;
  return { inicio, fim, dias: diasEntre(inicio, fim) + 1 };
}

// Agenciamentos das cargas em que o colaborador foi quem agenciou e que ainda
// não foram acertados. `creditos`: os créditos de agenciamento já lançados
// (de qualquer acerto).
export function agenciamentosPendentes(fretes, colaboradorId, creditos = [], ate = null) {
  const feitos = new Set(creditos.filter((l) => l.subtipo === 'agenciamento' && l.frete_id && !l.deleted_at).map((l) => l.frete_id));
  // Carga importada: o agenciamento foi acertado nos programas antigos.
  return fretes
    .filter((f) => !f.deleted_at && !f.importacao_id && f.agenciado_por === 'colaborador' && f.agenciador_id === colaboradorId
      && Number(f.agenciamento_valor) > 0 && !feitos.has(f.id) && (!ate || f.data_carregamento <= ate))
    .sort(crescente('data_carregamento'))
    .map((f) => ({
      subtipo: 'agenciamento',
      viagem_id: null,
      frete_id: f.id,
      data: f.data_carregamento,
      valor: Number(f.agenciamento_valor),
      descricao: `Agenciamento · carga de ${dataBR(f.data_carregamento)} (frete ${dinheiro(f.valor_frete)})`,
    }));
}

// Fixo mensal pago no acerto (o pago pela folha não entra): um por mês, do
// mês do último acerto (ou do mês do acerto, no primeiro) até o mês do
// acerto, fora os meses já pagos.
export function fixosPendentes(regras, colaboradorId, ate, { creditos = [], ultimoAcerto = null } = {}) {
  const pagos = new Set(creditos.filter((l) => l.subtipo === 'fixo_mensal' && !l.deleted_at).map((l) => l.data.slice(0, 7)));
  const fim = `${ate.slice(0, 7)}-01`;
  let mes = `${(ultimoAcerto?.data ?? ate).slice(0, 7)}-01`;
  const linhas = [];
  for (let i = 0; mes <= fim && i < 24; i++, mes = somarMeses(mes, 1)) {
    if (pagos.has(mes.slice(0, 7))) continue;
    // A regra que vale no último dia do mês (no mês do acerto, no dia dele).
    const dia = mes.slice(0, 7) === ate.slice(0, 7) ? ate : diaAnterior(somarMeses(mes, 1));
    const regra = regraValendo(regras, 'fixo_mensal', { colaboradorId, dia });
    if (!regra || regra.fixo_pagamento !== 'acerto' || !(Number(regra.valor) > 0)) continue;
    linhas.push({ subtipo: 'fixo_mensal', viagem_id: null, frete_id: null, data: mes, valor: Number(regra.valor), descricao: `Fixo de ${mesCurto(mes)}` });
  }
  return linhas;
}

function somarDia(iso, n) {
  const [a, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, d + n)).toISOString().slice(0, 10);
}
const diaAnterior = (iso) => somarDia(iso, -1);
const diaSeguinte = (iso) => somarDia(iso, 1);

// Saldo do acerto.
export function saldoDoAcerto({ saldoAnterior = 0, vales = [], despesas = [], creditos = [], descontos = [] }) {
  const soma = (lista) => lista.reduce((s, x) => s + centavos(x.valor), 0);
  const c = { anterior: centavos(saldoAnterior), vales: soma(vales), despesas: soma(despesas), creditos: soma(creditos), descontos: soma(descontos) };
  return {
    saldoAnterior: reais(c.anterior),
    vales: reais(c.vales),
    despesas: reais(c.despesas),
    creditos: reais(c.creditos),
    descontos: reais(c.descontos),
    saldo: reais(c.anterior + c.vales - c.despesas - c.creditos + c.descontos),
  };
}

// Frase do resultado: quem paga quem.
export function resultadoDoSaldo(saldo, nome = 'o colaborador') {
  if (centavos(saldo) === 0) return 'Acerto zerado: ninguém deve nada.';
  if (saldo < 0) return `A empresa paga ${dinheiro(-saldo)} a ${nome}.`;
  return `${nome.charAt(0).toUpperCase()}${nome.slice(1)} devolve ${dinheiro(saldo)} à empresa.`;
}

// ---------------------------------------------------------------------
// Prévia: tudo o que está pendente do colaborador até a data, com o que
// entra marcado. Quem chama pode desmarcar e recalcular.
//   viagens:      todas as da empresa (as do colaborador são filtradas)
//   fretes:       todas as cargas
//   lancamentos:  todos os lançamentos (os do colaborador são filtrados)
//   ultimoAcerto: o último acerto dele (ou null)
// ---------------------------------------------------------------------
export function previaDoAcerto({ colaboradorId, ate, viagens, fretes, lancamentos, regras, ultimoAcerto = null }) {
  const vivos = (lista) => lista.filter((x) => !x.deleted_at);
  const todosCreditos = vivos(lancamentos).filter((l) => l.tipo === 'credito');
  // Viagens dele que ainda não foram acertadas por inteiro: as encerradas e as
  // em andamento entram pela data (cargas carregadas até a data do acerto).
  // O histórico importado já foi acertado nos programas antigos: fica de fora
  // (o saldo de implantação, não: ele entra no primeiro acerto).
  const dele = vivos(viagens)
    .filter((v) => v.motorista_id === colaboradorId && (v.status === 'aberta' || v.status === 'encerrada') && !v.acerto_id && !v.importacao_id)
    .sort(crescente('data_saida'));
  const pendentes = vivos(lancamentos)
    .filter((l) => l.colaborador_id === colaboradorId && !l.acerto_id && l.data <= ate && l.origem !== 'importacao');

  const montadas = dele.filter((v) => v.data_saida <= ate).map((v) => {
    const daViagem = vivos(fretes).filter((f) => f.viagem_id === v.id && !f.acerto_id).sort(crescente('data_carregamento'));
    const cargas = daViagem.filter((f) => f.data_carregamento <= ate);
    const creditos = creditosDaViagem(v, cargas, regras, { jaLancados: todosCreditos, ate });
    // Encerrada até a data: depois deste acerto ela fica acertada (travada).
    const fecha = v.status === 'encerrada' && Boolean(v.data_chegada) && v.data_chegada <= ate;
    return {
      viagem: v,
      cargas,
      creditos,
      fecha,
      cargasDepois: daViagem.length - cargas.length,
      jaAcertouParte: vivos(fretes).some((f) => f.viagem_id === v.id && f.acerto_id)
        || todosCreditos.some((l) => l.viagem_id === v.id && l.acerto_id),
      incluir: true,
    };
  });
  const viagensDaPrevia = montadas.filter((x) => x.fecha || x.cargas.length > 0 || x.creditos.length > 0);
  // Os vales e despesas das viagens dele (em andamento também) entram marcados.
  const idsDele = new Set(dele.map((v) => v.id));
  const entra = (l) => !l.viagem_id || idsDele.has(l.viagem_id);

  const item = (l) => ({ lancamento: l, incluir: entra(l) });
  return {
    colaboradorId,
    ate,
    bloqueio: ultimoAcerto && ultimoAcerto.status === 'fechado' ? ultimoAcerto : null,
    saldoAnterior: ultimoAcerto?.quitacao === 'transportada' ? Number(ultimoAcerto.saldo) : 0,
    viagens: viagensDaPrevia,
    // Em andamento sem nada até a data (só para avisar na tela).
    semNada: montadas.filter((x) => !viagensDaPrevia.includes(x)).map((x) => x.viagem),
    // Saíram depois da data do acerto (ficam para o próximo).
    depois: dele.filter((v) => v.data_saida > ate),
    // Agenciamento do parceiro fica a pagar quando o adiantamento do frete é
    // recebido (ou logo, se a carga não tem adiantamento).
    agenciamentos: agenciamentosPendentes(fretes, colaboradorId, todosCreditos, ate).map((c) => {
      const frete = fretes.find((f) => f.id === c.frete_id);
      return { credito: c, frete, incluir: Boolean(frete?.adto_recebido_em) || !(Number(frete?.adto_valor) > 0) };
    }),
    fixos: fixosPendentes(regras, colaboradorId, ate, { creditos: todosCreditos.filter((l) => l.colaborador_id === colaboradorId), ultimoAcerto })
      .map((c) => ({ credito: c, incluir: true })),
    vales: pendentes.filter((l) => l.tipo === 'vale').sort(crescente('data')).map(item),
    despesas: pendentes.filter((l) => l.tipo === 'despesa' && l.pago_por === 'colaborador').sort(crescente('data')).map(item),
    creditos: pendentes.filter((l) => l.tipo === 'credito').sort(crescente('data')).map(item),
    descontos: pendentes.filter((l) => l.tipo === 'desconto').sort(crescente('data')).map(item),
  };
}

// O que entrou num acerto, a partir das listas inteiras. A viagem que estava
// em andamento entra em parte (`parcial`): as cargas carregadas até a data.
export function itensDoAcerto(acertoId, { viagens = [], fretes = [], lancamentos = [] }) {
  const vivos = (lista) => lista.filter((x) => !x.deleted_at);
  const cargas = vivos(fretes).filter((f) => f.acerto_id === acertoId);
  const lancs = vivos(lancamentos).filter((l) => l.acerto_id === acertoId);
  const ids = new Set([...cargas.map((f) => f.viagem_id), ...lancs.filter((l) => l.tipo === 'credito' && l.viagem_id).map((l) => l.viagem_id)]);
  return {
    viagens: vivos(viagens)
      .filter((v) => v.acerto_id === acertoId || ids.has(v.id))
      .sort((a, b) => (a.data_saida < b.data_saida ? -1 : a.data_saida > b.data_saida ? 1 : 0))
      .map((v) => ({ ...v, parcial: v.acerto_id !== acertoId })),
    fretes: cargas,
    lancamentos: lancs,
  };
}

// Há algo esperando acerto (marcado ou não)?
export function temPendencia(previa) {
  const itens = previa.viagens.length + previa.agenciamentos.length + previa.fixos.length + previa.vales.length
    + previa.despesas.length + previa.creditos.length + previa.descontos.length;
  return itens > 0 || centavos(previa.saldoAnterior) !== 0;
}

// O que a prévia (com as marcações) manda para o fechamento e os totais.
export function montarFechamento(previa) {
  const marcados = (lista) => lista.filter((x) => x.incluir);
  const viagens = marcados(previa.viagens);
  const novos = [
    ...viagens.flatMap((v) => v.creditos),
    ...marcados(previa.agenciamentos).map((a) => a.credito),
    ...marcados(previa.fixos).map((f) => f.credito),
  ];
  const lancs = {
    vales: marcados(previa.vales).map((x) => x.lancamento),
    despesas: marcados(previa.despesas).map((x) => x.lancamento),
    creditos: marcados(previa.creditos).map((x) => x.lancamento),
    descontos: marcados(previa.descontos).map((x) => x.lancamento),
  };
  const totais = saldoDoAcerto({
    saldoAnterior: previa.saldoAnterior,
    vales: lancs.vales,
    despesas: lancs.despesas,
    creditos: [...lancs.creditos, ...novos],
    descontos: lancs.descontos,
  });
  const vazio = viagens.length === 0 && novos.length === 0 && Object.values(lancs).every((l) => l.length === 0) && centavos(previa.saldoAnterior) === 0;
  return {
    viagens: viagens.map((v) => v.viagem.id),
    fretes: viagens.flatMap((v) => v.cargas.map((f) => f.id)),
    lancamentos: Object.values(lancs).flat().map((l) => l.id),
    creditos: novos.map((c) => ({
      subtipo: c.subtipo, valor: c.valor, data: c.data, viagem_id: c.viagem_id ?? null, frete_id: c.frete_id ?? null, descricao: c.descricao,
    })),
    totais,
    vazio,
  };
}
